import React, { useState, useRef, useEffect } from 'react';
import { Pen, Type, Upload, Trash, Check, Save, AlertCircle, Edit3 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { API_URL } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';

const SignatureSettingsTab = () => {
  const { user } = useAuth();
  
  // SIGNATURE CREATION STATES
  const [signatureType, setSignatureType] = useState('drawn');
  const [typedSignature, setTypedSignature] = useState('');
  const [typedSignatureFont, setTypedSignatureFont] = useState('Inter');
  const [fontCategory, setFontCategory] = useState('professional');
  const [imageSignature, setImageSignature] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // DRAWING CANVAS STATES
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  
  // COMPONENT STATES
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [defaultSignature, setDefaultSignature] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // REFS
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  
  // Available fonts for typed signatures
  const availableFonts = [
    // Professional & Business
    { name: 'Inter', css: "'Inter', sans-serif", category: 'professional' },
    { name: 'Roboto', css: "'Roboto', sans-serif", category: 'professional' },
    { name: 'Open Sans', css: "'Open Sans', sans-serif", category: 'professional' },
    { name: 'Lato', css: "'Lato', sans-serif", category: 'professional' },
    { name: 'Montserrat', css: "'Montserrat', sans-serif", category: 'professional' },
    { name: 'Poppins', css: "'Poppins', sans-serif", category: 'professional' },
    { name: 'Source Sans Pro', css: "'Source Sans Pro', sans-serif", category: 'professional' },
    
    // Elegant Scripts
    { name: 'Dancing Script', css: "'Dancing Script', cursive", category: 'elegant' },
    { name: 'Great Vibes', css: "'Great Vibes', cursive", category: 'elegant' },
    { name: 'Allura', css: "'Allura', cursive", category: 'elegant' },
    { name: 'Pinyon Script', css: "'Pinyon Script', cursive", category: 'elegant' },
    
    // Modern Scripts
    { name: 'Alex Brush', css: "'Alex Brush', cursive", category: 'modern' },
    { name: 'Kaushan Script', css: "'Kaushan Script', cursive", category: 'modern' },
    { name: 'Yellowtail', css: "'Yellowtail', cursive", category: 'modern' },
    
    // Casual Handwriting
    { name: 'Pacifico', css: "'Pacifico', cursive", category: 'casual' },
    { name: 'Satisfy', css: "'Satisfy', cursive", category: 'casual' },
    { name: 'Caveat', css: "'Caveat', cursive", category: 'casual' },
    { name: 'Indie Flower', css: "'Indie Flower', cursive", category: 'casual' },
    
    // Classic & Professional
    { name: 'Sacramento', css: "'Sacramento', cursive", category: 'classic' },
    { name: 'Norican', css: "'Norican', cursive", category: 'classic' },
    { name: 'Kalam', css: "'Kalam', sans-serif", category: 'classic' },
  ];
  
  // Initialize fonts on component mount
  useEffect(() => {
    // Load Google Fonts
    document.head.insertAdjacentHTML('beforeend', `
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Roboto:wght@400;500;700&family=Open+Sans:wght@400;600;700&family=Lato:wght@400;700&family=Montserrat:wght@400;600;700&family=Poppins:wght@400;500;600;700&family=Source+Sans+Pro:wght@400;600;700&family=Dancing+Script&family=Great+Vibes&family=Pacifico&family=Sacramento&family=Satisfy&family=Allura&family=Alex+Brush&family=Kaushan+Script&family=Yellowtail&family=Pinyon+Script&family=Caveat&family=Indie+Flower&family=Norican&family=Kalam:wght@400;700&display=swap" rel="stylesheet">
    `);
    
    fetchDefaultSignature();
  }, []);

  // Initialize canvas when switching to drawing mode
  useEffect(() => {
    if (signatureType === 'drawn') {
      setTimeout(() => {
        initializeCanvas();
      }, 100);
    } else {
      contextRef.current = null;
    }
  }, [signatureType]);

  // Reinitialize canvas when window resizes
  useEffect(() => {
    const handleResize = () => {
      if (signatureType === 'drawn' && canvasRef.current) {
        contextRef.current = null; // Reset context
        setTimeout(() => {
          initializeCanvas();
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [signatureType]);

  // Effect to automatically select first font when category changes
  useEffect(() => {
    const filteredFonts = availableFonts.filter(font => font.category === fontCategory);
    if (filteredFonts.length > 0 && !filteredFonts.find(f => f.name === typedSignatureFont)) {
      setTypedSignatureFont(filteredFonts[0].name);
    }
  }, [fontCategory, typedSignatureFont]);

  // Track changes for unsaved work warning
  useEffect(() => {
    let hasNewChanges = false;
    
    if (signatureType === 'drawn' && hasDrawn) {
      hasNewChanges = true;
    } else if (signatureType === 'typed' && typedSignature.trim()) {
      hasNewChanges = true;
    } else if (signatureType === 'image' && imageSignature) {
      hasNewChanges = true;
    }
    
    setHasChanges(hasNewChanges);
  }, [signatureType, hasDrawn, typedSignature, imageSignature]);

  const fetchDefaultSignature = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/users/default-signature`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch default signature');
      }
      
      const data = await response.json();
      setDefaultSignature(data.defaultSignature);
      
      // If there's a default signature, load it into the form
      if (data.defaultSignature) {
        const sig = data.defaultSignature;
        setSignatureType(sig.type);
        if (sig.type === 'typed') {
          // Use originalText for typed signatures, not the base64 image data
          setTypedSignature(sig.originalText || '');
          setTypedSignatureFont(sig.font || 'Inter');
        } else if (sig.type === 'image' || sig.type === 'drawn') {
          setImageSignature(sig.data);
          setImagePreview(sig.data);
        }
      }
    } catch (error) {
      console.error('Error fetching default signature:', error);
      // Not showing error toast here as user might not have a signature yet
    } finally {
      setIsLoading(false);
    }
  };

  const initializeCanvas = () => {
    if (canvasRef.current && !contextRef.current) {
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * 2;
      canvas.height = rect.height * 2;
      
      const context = canvas.getContext('2d');
      context.scale(2, 2);
      context.lineCap = 'round';
      context.strokeStyle = '#0F172A';
      context.lineWidth = 2;
      contextRef.current = context;
    }
  };

  const startDrawing = ({ nativeEvent }) => {
    if (!contextRef.current) {
      initializeCanvas();
    }
    
    if (!contextRef.current) return;
    
    const { offsetX, offsetY } = nativeEvent;
    contextRef.current.beginPath();
    contextRef.current.moveTo(offsetX, offsetY);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = ({ nativeEvent }) => {
    if (!isDrawing || !contextRef.current) return;
    
    const { offsetX, offsetY } = nativeEvent;
    contextRef.current.lineTo(offsetX, offsetY);
    contextRef.current.stroke();
  };

  const stopDrawing = () => {
    if (!contextRef.current) return;
    contextRef.current.closePath();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    if (contextRef.current && canvasRef.current) {
      // Clear using the scaled dimensions
      contextRef.current.clearRect(0, 0, canvasRef.current.width / 2, canvasRef.current.height / 2);
      setHasDrawn(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const imageData = event.target.result;
      setImageSignature(imageData);
      setImagePreview(imageData);
    };
    reader.readAsDataURL(file);
  };

  const createTypedSignatureImage = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 150;
    const ctx = canvas.getContext('2d');
    
    // Set background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Set text style
    const selectedFont = availableFonts.find(f => f.name === typedSignatureFont);
    ctx.fillStyle = '#0F172A';
    ctx.font = `36px ${selectedFont?.css || "'Inter', sans-serif"}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Draw text
    ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);
    
    return canvas.toDataURL('image/png');
  };

  const saveSignature = async () => {
    setIsSaving(true);
    try {
      let signatureData;
      let signatureFont = null;
      
      if (signatureType === 'drawn') {
        if (!hasDrawn) {
          toast.error('Please draw your signature first');
          return;
        }
        signatureData = canvasRef.current.toDataURL('image/png');
      } else if (signatureType === 'typed') {
        if (!typedSignature.trim()) {
          toast.error('Please enter your signature text');
          return;
        }
        signatureData = createTypedSignatureImage();
        signatureFont = typedSignatureFont;
      } else if (signatureType === 'image') {
        if (!imageSignature) {
          toast.error('Please upload a signature image');
          return;
        }
        signatureData = imageSignature;
      }
      
      const payload = {
        signatureType,
        signatureData,
        signatureFont,
        originalText: signatureType === 'typed' ? typedSignature : null
      };
      
      const response = await fetch(`${API_URL}/users/default-signature`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save signature');
      }
      
      const data = await response.json();
      setDefaultSignature(data.defaultSignature);
      setHasChanges(false);
      toast.success('Default signature saved successfully!');
    } catch (error) {
      console.error('Error saving signature:', error);
      toast.error('Failed to save signature. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteSignature = async () => {
    if (!window.confirm('Are you sure you want to delete your default signature?')) {
      return;
    }
    
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/users/default-signature`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete signature');
      }
      setDefaultSignature(null);
      setHasChanges(false);
      
      // Reset form
      setSignatureType('drawn');
      setTypedSignature('');
      setImageSignature(null);
      setImagePreview(null);
      setHasDrawn(false);
      clearCanvas();
      
      toast.success('Default signature deleted successfully!');
    } catch (error) {
      console.error('Error deleting signature:', error);
      toast.error('Failed to delete signature. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const getFilteredFonts = () => {
    return availableFonts.filter(font => font.category === fontCategory);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Signature Display */}
      {defaultSignature && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Edit3 className="w-5 h-5 mr-2 text-blue-500" />
            Current Default Signature
          </h3>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <img 
                  src={defaultSignature.data} 
                  alt="Current signature" 
                  className="max-h-16 max-w-32"
                />
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  Type: <span className="capitalize font-medium">{defaultSignature.type}</span>
                </p>
                {defaultSignature.font && (
                  <p className="text-sm text-gray-600">
                    Font: <span className="font-medium">{defaultSignature.font}</span>
                  </p>
                )}
                <p className="text-sm text-gray-600">
                  Created: {new Date(defaultSignature.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            
            <button
              onClick={deleteSignature}
              disabled={isSaving}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
            >
              <Trash className="w-4 h-4 mr-2" />
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Signature Creation */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Edit3 className="w-5 h-5 mr-2 text-blue-500" />
          {defaultSignature ? 'Update Default Signature' : 'Create Default Signature'}
        </h3>
        
        {/* Signature Type Tabs */}
        <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setSignatureType('drawn')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              signatureType === 'drawn'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Pen className="w-4 h-4 mx-auto mb-1" />
            Draw
          </button>
          <button
            onClick={() => setSignatureType('typed')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              signatureType === 'typed'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Type className="w-4 h-4 mx-auto mb-1" />
            Type
          </button>
          <button
            onClick={() => setSignatureType('image')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              signatureType === 'image'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Upload className="w-4 h-4 mx-auto mb-1" />
            Upload
          </button>
        </div>

        {/* Signature Creation Content */}
        {signatureType === 'drawn' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-3">Draw your signature below:</p>
              <canvas
                ref={canvasRef}
                className="border border-gray-200 rounded-md bg-white cursor-crosshair w-full"
                style={{ width: '100%', height: '150px' }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
              />
              <div className="flex justify-between items-center mt-3">
                <button
                  onClick={clearCanvas}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 flex items-center"
                >
                  <Trash className="w-4 h-4 mr-1" />
                  Clear
                </button>
                <p className="text-xs text-gray-500">
                  {hasDrawn ? 'Signature drawn' : 'Click and drag to draw'}
                </p>
              </div>
            </div>
          </div>
        )}

        {signatureType === 'typed' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Signature Text
              </label>
              <input
                type="text"
                value={typedSignature}
                onChange={(e) => setTypedSignature(e.target.value)}
                placeholder="Enter your full name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Font Style
              </label>
              <div className="flex space-x-2 mb-3">
                {['professional', 'elegant', 'modern', 'casual', 'classic'].map((category) => (
                  <button
                    key={category}
                    onClick={() => setFontCategory(category)}
                    className={`px-3 py-1 text-sm rounded-md capitalize ${
                      fontCategory === category
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
              
              <select
                value={typedSignatureFont}
                onChange={(e) => setTypedSignatureFont(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {getFilteredFonts().map((font) => (
                  <option key={font.name} value={font.name}>
                    {font.name}
                  </option>
                ))}
              </select>
            </div>
            
            {typedSignature && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <p className="text-sm text-gray-600 mb-2">Preview:</p>
                <div 
                  className="text-3xl text-center py-4"
                  style={{ 
                    fontFamily: availableFonts.find(f => f.name === typedSignatureFont)?.css || "'Inter', sans-serif" 
                  }}
                >
                  {typedSignature}
                </div>
              </div>
            )}
          </div>
        )}

        {signatureType === 'image' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Signature Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {imagePreview && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <p className="text-sm text-gray-600 mb-2">Preview:</p>
                <div className="flex justify-center">
                  <img 
                    src={imagePreview} 
                    alt="Signature preview" 
                    className="max-h-32 max-w-full border border-gray-300 rounded"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            onClick={saveSignature}
            disabled={isSaving || !hasChanges}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Signature'}
          </button>
        </div>
      </div>

      {/* Info Panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">How Default Signatures Work</p>
            <p>
              Your default signature will be automatically used when signing documents. 
              You can still edit it before finalizing any signature. This signature is 
              stored securely and encrypted.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignatureSettingsTab; 